import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PublicSubscriptionDataSourceService,
  PublicSubscriptionShippingAddressForm,
  SubscriptionNumber,
} from '@web/shared/module/shop';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { PublicSubscriptionShippingAddressFormComponent } from '../public-subscription-shipping-address-form/public-subscription-shipping-address-form.component';

@Component({
  selector: 'app-public-subscription-shipping-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertsComponent,
    SpinnerFilledComponent,
    PublicSubscriptionShippingAddressFormComponent,
  ],
  templateUrl: './public-subscription-shipping-address-edit.component.html',
  styleUrl: './public-subscription-shipping-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicSubscriptionShippingAddressEditComponent {
  public readonly number = input.required<SubscriptionNumber>();
  public readonly submitEvent = output<void>();
  public readonly cancelEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly number$ = this.numberSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(PublicSubscriptionDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleSubmitClickEvent(
    data: PublicSubscriptionShippingAddressForm,
  ): void {
    this.markAsPending();

    this.subscriptionDataSource
      .updateShippingAddressByFormData({
        number: this.number(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  public handleCancelClickEvent(): void {
    this.cancelEvent.emit();
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private numberSource(): Observable<SubscriptionNumber> {
    return toObservable(this.number);
  }

  private dataSource(): Observable<PublicSubscriptionShippingAddressForm> {
    return this.number$.pipe(
      switchMap((number) =>
        this.subscriptionDataSource.fetchShippingAddressFormByNumber(number),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
